import {
  Box,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Grid,
  Flex,
} from '@ouster/webviz-react-components';

import { format, isAfter, subDays, subMonths } from 'date-fns';
import { useState } from 'react';
import {
  LuCalendar,
  LuChevronDown,
  LuCheck,
  LuCalendarDays,
} from 'react-icons/lu';

interface DateRange {
  start: Date;
  end: Date;
  label: string;
}

const PRESET_RANGES: { label: string; days?: number; months?: number }[] = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 6 months', months: 6 },
  { label: 'Last 12 months', months: 12 },
];
interface NativeDateInputProps {
  label: string;
  value: Date;
  min?: Date;
  onChange: (dateString: string) => void;
  containerProps?: React.ComponentProps<typeof Box>;
}

const NativeDateInput = ({
  label,
  value,
  min,
  onChange,
  containerProps,
}: NativeDateInputProps) => {
  const dateString = format(value, 'yyyy-MM-dd');
  const minString = min ? format(min, 'yyyy-MM-dd') : undefined;

  return (
    <Box {...containerProps}>
      <Box fontSize="xs" color="gray.400" mb={1}>
        {label}
      </Box>
      <Box position="relative" width="100%">
        <input
          type="date"
          value={dateString}
          min={minString}
          onChange={(e) => {
            onChange(e.target.value);
            // once a value is selected to clear the blue highlight.
            e.target.blur();
          }}
          onClick={(e) => {
            e.currentTarget.showPicker();
          }}
          style={{
            background: 'transparent',
            border: '1px solid #4A5568',
            borderRadius: '4px',
            padding: '6px 32px 6px 10px',
            fontSize: '14px',
            width: '100%',
            colorScheme: 'dark',
            cursor: 'pointer',
          }}
        />

        {/* Layer 3: Custom Icon */}
        <Box
          position="absolute"
          right="10px"
          top="50%"
          transform="translateY(-50%)"
          pointerEvents="none"
          color="gray.500"
          zIndex={2}
        >
          <LuCalendarDays size={14} />
        </Box>
      </Box>
    </Box>
  );
};

export const DateRangePicker = ({
  range,
  onChange,
}: {
  range: DateRange;
  onChange: (range: DateRange) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingRange, setPendingRange] = useState(range);

  // Sync pending range when popover opens
  const handleOpen = () => {
    setPendingRange(range);
    onOpen();
  };

  const handlePreset = (preset: {
    label: string;
    days?: number;
    months?: number;
  }) => {
    const end = new Date();
    const start = preset.months
      ? subMonths(end, preset.months)
      : subDays(end, preset.days ?? 0);
    const newRange = { start, end, label: preset.label };
    setPendingRange(newRange);
    onChange(newRange);
    onClose();
  };

  const handleDateChange = (type: 'start' | 'end', dateString: string) => {
    if (!dateString) return;

    // Use forward slashes to force Local Time parsing instead of UTC
    const selectedDate = new Date(dateString.replace(/-/g, '/'));

    let newStart = pendingRange.start;
    let newEnd = pendingRange.end;

    if (type === 'start') {
      newStart = selectedDate;
      // If start is now after end, push end to match start
      if (isAfter(newStart, newEnd)) {
        newEnd = newStart;
      }
    } else {
      newEnd = selectedDate;
    }

    setPendingRange({
      start: newStart,
      end: newEnd,
      label: '',
    });
  };

  const handleApply = () => {
    onChange(pendingRange);
    onClose();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={onClose}
      placement="bottom-end"
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          width="260px"
          justifyContent="space-between"
          borderColor={isOpen ? 'blue.400' : 'blue.500'}
          _hover={{ bg: 'gray.800' }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <LuCalendar color="gray.500" />
            <Box as="p" fontSize="sm" fontWeight="medium" color="gray.200">
              {range.label ||
                `${format(range.start, 'MMM d, yyyy')} - ${format(
                  range.end,
                  'MMM d, yyyy'
                )}`}
            </Box>
          </Box>
          <LuChevronDown
            style={{
              transform: isOpen ? 'rotate(180deg)' : '',
              transition: 'transform 0.2s',
            }}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        bg="gray.900"
        border="1px solid"
        borderColor="gray.700"
        boxShadow="2xl"
        borderRadius={0}
        width={{ base: '320px', md: '420px' }}
        p={0}
      >
        <PopoverBody
          p={4}
          bg="gray.900"
          display="flex"
          borderColor="gray.300"
          flexDirection={{ base: 'column', md: 'row' }}
        >
          {/* Left Side: Presets */}
          <Grid
            width={{ base: 'full', md: '160px' }}
            pr={2}
            borderRight="1px solid"
            borderColor="gray.700"
          >
            <Box
              fontSize="sm"
              fontWeight="bold"
              color="gray.400"
              textTransform="uppercase"
              mb={4}
            >
              Presets
            </Box>

            <Grid gap={1}>
              {PRESET_RANGES.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  justifyContent="space-between"
                  color={range.label === preset.label ? 'blue.400' : 'gray.400'}
                  bg={range.label === preset.label ? 'blue.900' : 'transparent'}
                  _hover={{ bg: 'gray.800' }}
                  onClick={() => handlePreset(preset)}
                >
                  {preset.label}
                  {range.label === preset.label && <LuCheck />}
                </Button>
              ))}
            </Grid>
          </Grid>

          {/* Right Side: Custom Inputs */}
          <Flex pl={6} flex={1} direction="column">
            <Box
              fontSize="sm"
              fontWeight="bold"
              color="gray.400"
              textTransform="uppercase"
              mb={6}
            >
              Custom Range
            </Box>

            <Flex direction="column" flex={1} justifyContent="space-between">
              <NativeDateInput
                label="Start Date"
                value={pendingRange.start}
                onChange={(val) => handleDateChange('start', val)}
              />

              <NativeDateInput
                label="End Date"
                value={pendingRange.end}
                min={pendingRange.start}
                onChange={(val) => handleDateChange('end', val)}
              />
              <Button size="sm" mt={2} onClick={handleApply}>
                Apply Range
              </Button>
            </Flex>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
